/**
 * QuickDataEntry — universal floating-button entry modal accessible from anywhere.
 * Dynamic forms per module + file uploads + auto-route to correct backend endpoint.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Zap, X, Upload, Search, ChevronRight, FileText, ListChecks,
  DollarSign, TrendingDown, Award, Users, GraduationCap, Wallet,
  Boxes, Wheat, Wrench, Trophy, HeartHandshake, BookOpen, Hammer,
  Plus, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { compressImageToBase64 } from '../lib/imageCompress';
import { cn } from '../lib/utils';
import { TaxonomyFieldsEditor } from './finance/TaxonomyFieldsEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Each module: id, label, icon, accent color, roles, fields, endpoint, transform
const MODULES = [
  {
    id: 'income', label: 'Income', icon: DollarSign, color: 'bg-emerald-100 text-emerald-700',
    roles: ['admin', 'finance_officer'],
    endpoint: '/api/income',
    transform: (v) => {
      const out = { ...v };
      if (v.currency && v.currency !== 'UGX' && v.original_amount) {
        out.amount = (parseFloat(v.original_amount) || 0) * (parseFloat(v.exchange_rate) || 1);
      }
      return out;
    },
    fields: [
      { name: 'source', label: 'Source', type: 'select', required: true,
        options: ['school_fees','donations','grants','chicken_sales','garden_produce','masonry_sales','maize_sales','loan_interest','fundraising','sponsorship','training_fees'] },
      { name: 'program', label: 'Program / Project', type: 'select',
        options: [
          { value: 'general', label: 'General' },
          { value: 'school', label: 'School' },
          { value: 'women', label: 'Women Program' },
          { value: 'sponsorship', label: 'Sponsorship Program' },
        ] },
      { name: 'currency', label: 'Currency', type: 'select', defaultValue: 'UGX',
        options: [
          { value: 'UGX', label: 'Uganda Shillings (UGX)' },
          { value: 'USD', label: 'US Dollars (USD)' },
          { value: 'EUR', label: 'Euros (EUR)' },
          { value: 'GBP', label: 'UK Pounds (GBP)' },
          { value: 'OTHER', label: 'Other' },
        ] },
      { name: 'original_amount', label: 'Original Amount (foreign currency — optional if UGX)', type: 'number' },
      { name: 'exchange_rate', label: 'Exchange Rate (→ UGX)', type: 'number', defaultValue: 1 },
      { name: 'amount', label: 'Amount (UGX)', type: 'number', required: true },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'description', label: 'Description', type: 'text' },
      { name: 'receipt_image', label: 'Receipt', type: 'file' },
    ],
  },
  {
    id: 'school_fees', label: 'School Fees', icon: GraduationCap, color: 'bg-blue-100 text-blue-700',
    roles: ['admin', 'finance_officer', 'school_head'],
    endpoint: '/api/income',
    transform: (v) => ({ ...v, source: 'school_fees' }),
    fields: [
      { name: 'student_first_name', label: 'Student First Name', type: 'text', required: true },
      { name: 'student_last_name', label: 'Student Last Name', type: 'text', required: true },
      { name: 'student_class', label: 'Class', type: 'text' },
      { name: 'parent_guardian_name', label: 'Parent/Guardian', type: 'text' },
      { name: 'amount', label: 'Amount Paid (UGX)', type: 'number', required: true },
      { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'receipt_image', label: 'Receipt', type: 'file' },
    ],
  },
  {
    id: 'grant', label: 'Grant', icon: Award, color: 'bg-amber-100 text-amber-700',
    roles: ['admin', 'finance_officer'],
    endpoint: '/api/income',
    transform: (v) => {
      const ugx = (parseFloat(v.original_amount) || 0) * (parseFloat(v.exchange_rate) || 1);
      return { ...v, source: 'grants', amount: ugx };
    },
    fields: [
      { name: 'grant_name', label: 'Grant Name', type: 'text', required: true },
      { name: 'date', label: 'Date Received', type: 'date', required: true },
      { name: 'currency', label: 'Currency', type: 'select', required: true, defaultValue: 'UGX',
        options: [
          { value: 'USD', label: 'US Dollars (USD)' },
          { value: 'EUR', label: 'Euros (EUR)' },
          { value: 'GBP', label: 'UK Pounds (GBP)' },
          { value: 'UGX', label: 'Uganda Shillings (UGX)' },
          { value: 'OTHER', label: 'Other' },
        ] },
      { name: 'original_amount', label: 'Original Amount', type: 'number', required: true },
      { name: 'exchange_rate', label: 'Exchange Rate (→ UGX)', type: 'number', required: true, defaultValue: 1 },
      { name: 'receipt_image', label: 'Document', type: 'file' },
    ],
  },
  {
    id: 'expense', label: 'Expense', icon: TrendingDown, color: 'bg-red-100 text-red-700',
    roles: ['admin', 'finance_officer', 'project_manager'],
    endpoint: '/api/expenses',
    fields: [
      { name: 'taxonomy', label: 'Expense Category', type: 'taxonomy', required: true },
      { name: 'amount', label: 'Amount (UGX)', type: 'number', required: true },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'description', label: 'Description', type: 'text' },
      { name: 'receipt_image', label: 'Receipt', type: 'file' },
    ],
  },
  {
    id: 'chicken_sales', label: 'Poultry / Chicken Sales', icon: Wheat, color: 'bg-orange-100 text-orange-700',
    roles: ['admin', 'finance_officer', 'project_manager'],
    endpoint: '/api/income',
    transform: (v) => ({ ...v, source: 'chicken_sales', amount: parseFloat(v.payment_amount || 0) }),
    fields: [
      { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
      { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
      { name: 'payment_amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
      { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'receipt_image', label: 'Voucher/Receipt', type: 'file' },
    ],
  },
  {
    id: 'garden_produce', label: 'Garden Produce', icon: Wheat, color: 'bg-green-100 text-green-700',
    roles: ['admin', 'finance_officer', 'project_manager'],
    endpoint: '/api/income',
    transform: (v) => ({ ...v, source: 'garden_produce', amount: parseFloat(v.payment_amount || 0) }),
    fields: [
      { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
      { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
      { name: 'payment_amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
      { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'receipt_image', label: 'Voucher/Receipt', type: 'file' },
    ],
  },
  {
    id: 'masonry_sales', label: 'Masonry Sales', icon: Hammer, color: 'bg-stone-100 text-stone-700',
    roles: ['admin', 'finance_officer', 'project_manager'],
    endpoint: '/api/income',
    transform: (v) => ({ ...v, source: 'masonry_sales', amount: parseFloat(v.payment_amount || 0) }),
    fields: [
      { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
      { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
      { name: 'payment_amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
      { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'receipt_image', label: 'Voucher/Receipt', type: 'file' },
    ],
  },
  {
    id: 'loan', label: 'Loan', icon: Wallet, color: 'bg-purple-100 text-purple-700',
    roles: ['admin', 'loan_officer'],
    endpoint: '/api/loans',
    fields: [
      { name: 'borrower_name', label: 'Borrower Name', type: 'text', required: true },
      { name: 'borrower_phone', label: 'Phone', type: 'text' },
      { name: 'business_type', label: 'Business', type: 'text' },
      { name: 'loan_amount', label: 'Loan Amount (UGX)', type: 'number', required: true },
      { name: 'interest_rate', label: 'Interest %', type: 'number', defaultValue: 10 },
      { name: 'duration_months', label: 'Term (months)', type: 'number', defaultValue: 6 },
      { name: 'issue_date', label: 'Issue Date', type: 'date', required: true },
    ],
  },
  {
    id: 'sponsorship_child', label: 'Sponsored Child', icon: HeartHandshake, color: 'bg-pink-100 text-pink-700',
    roles: ['admin', 'project_manager', 'school_head'],
    endpoint: '/api/sponsorship/children',
    fields: [
      { name: 'name', label: 'Child Name', type: 'text', required: true },
      { name: 'grade', label: 'Class/Grade', type: 'text' },
      { name: 'school', label: 'School', type: 'text' },
      { name: 'sponsor_name', label: 'Sponsor', type: 'text' },
      { name: 'sponsor_contact', label: 'Sponsor Contact', type: 'text' },
      { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
    ],
  },
  {
    id: 'staff', label: 'Staff Member', icon: Users, color: 'bg-cyan-100 text-cyan-700',
    roles: ['admin', 'school_head'],
    endpoint: '/api/personnel/staff',
    fields: [
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'select', required: true,
        options: ['administrator', 'teaching', 'non_teaching'] },
      { name: 'subject', label: 'Subject (if teaching)', type: 'text' },
      { name: 'title', label: 'Title / Position', type: 'text' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'home_address', label: 'Address', type: 'text' },
    ],
  },
  {
    id: 'student', label: 'Student', icon: GraduationCap, color: 'bg-indigo-100 text-indigo-700',
    roles: ['admin', 'school_head'],
    endpoint: '/api/personnel/students',
    fields: [
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'school', label: 'School', type: 'select', required: true,
        options: ['suubi_primary', 'suubi_secondary'] },
      { name: 'student_class', label: 'Class', type: 'text' },
      { name: 'gender', label: 'Gender', type: 'select', options: ['male', 'female'] },
      { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
      { name: 'parent_names', label: 'Parent Names', type: 'text' },
      { name: 'parent_contact', label: 'Parent Contact', type: 'text' },
      { name: 'home_address', label: 'Address', type: 'text' },
    ],
  },
  {
    id: 'catchup_student', label: 'Catch-up Student', icon: BookOpen, color: 'bg-rose-100 text-rose-700',
    roles: ['admin', 'school_head', 'project_manager'],
    endpoint: '/api/catchup/students',
    fields: [
      { name: 'student_name', label: 'Student Name', type: 'text', required: true },
      { name: 'class_grade', label: 'Class', type: 'text' },
      { name: 'class_teacher', label: 'Class Teacher', type: 'text' },
      { name: 'parent_guardian_name', label: 'Parent', type: 'text' },
      { name: 'parent_contact', label: 'Parent Phone', type: 'text' },
      { name: 'circumstances', label: 'Circumstances', type: 'textarea' },
      { name: 'focus_subjects', label: 'Focus Subjects', type: 'text' },
    ],
  },
  {
    id: 'inventory', label: 'Inventory Item', icon: Boxes, color: 'bg-yellow-100 text-yellow-700',
    roles: ['admin', 'project_manager', 'staff'],
    endpoint: '/api/inventory',
    fields: [
      { name: 'name', label: 'Item Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'text' },
      { name: 'quantity', label: 'Quantity', type: 'number', required: true },
      { name: 'unit', label: 'Unit', type: 'text' },
      { name: 'min_stock', label: 'Min Stock Level', type: 'number' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'unit_cost', label: 'Unit Cost (UGX)', type: 'number' },
    ],
  },
  {
    id: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'bg-amber-100 text-amber-700',
    roles: ['admin', 'project_manager', 'staff'],
    endpoint: '/api/maintenance',
    fields: [
      { name: 'asset_id', label: 'Asset ID', type: 'text', required: true },
      { name: 'asset_name', label: 'Asset Name', type: 'text' },
      { name: 'maintenance_type', label: 'Type', type: 'select', required: true,
        options: ['preventive', 'corrective', 'inspection', 'other'] },
      { name: 'scheduled_date', label: 'Scheduled Date', type: 'date', required: true },
      { name: 'cost', label: 'Cost (UGX)', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    id: 'maize_purchase', label: 'Maize Purchase', icon: Wheat, color: 'bg-yellow-100 text-yellow-800',
    roles: ['admin', 'project_manager', 'finance_officer'],
    endpoint: '/api/maize/purchases',
    fields: [
      { name: 'farmer_id', label: 'Farmer (lookup by ID)', type: 'text', required: true },
      { name: 'quantity_kg', label: 'Quantity (kg)', type: 'number', required: true },
      { name: 'price_per_kg', label: 'Price per kg (UGX)', type: 'number', required: true },
      { name: 'quality_grade', label: 'Grade', type: 'select', options: ['A', 'B', 'C'] },
      { name: 'date', label: 'Date', type: 'date', required: true },
      { name: 'notes', label: 'Notes', type: 'text' },
    ],
  },
  {
    id: 'club_member', label: 'Club Member', icon: Trophy, color: 'bg-fuchsia-100 text-fuchsia-700',
    roles: ['admin', 'school_head', 'project_manager'],
    endpoint: '/api/school-clubs/members',
    fields: [
      { name: 'club_id', label: 'Club ID', type: 'text', required: true },
      { name: 'student_name', label: 'Student Name', type: 'text', required: true },
      { name: 'role', label: 'Role', type: 'select',
        options: ['President', 'Vice President', 'Secretary', 'Treasurer', 'Member'] },
      { name: 'grade_class', label: 'Class', type: 'text' },
      { name: 'parent_name', label: 'Parent', type: 'text' },
      { name: 'parent_contact', label: 'Parent Phone', type: 'text' },
    ],
  },
];

const QuickDataEntry = ({ open, onOpenChange }) => {
  const { user, hasRole } = useAuth();
  const [moduleId, setModuleId] = useState(null);
  const [form, setForm] = useState({});
  const [taxonomy, setTaxonomy] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/finance/taxonomy`).then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  const availableModules = useMemo(
    () => MODULES.filter((m) => hasRole(m.roles)),
    [hasRole],
  );

  const selected = MODULES.find((m) => m.id === moduleId);

  // Initialize form when module changes
  useEffect(() => {
    if (selected) {
      const init = {};
      selected.fields.forEach((f) => {
        if (f.defaultValue !== undefined) init[f.name] = f.defaultValue;
        if (f.type === 'date' && !init[f.name]) init[f.name] = new Date().toISOString().slice(0, 10);
      });
      setForm(init);
    }
  }, [moduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist recents in localStorage
  useEffect(() => {
    try {
      const r = JSON.parse(localStorage.getItem('suubi_qde_recent') || '[]');
      setRecent(r);
    } catch (_) { /* best-effort */ }
  }, [open]);

  const handleFile = async (fieldName, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    try {
      let dataUrl;
      if (file.type.startsWith('image/')) {
        dataUrl = await compressImageToBase64(file, { maxDimension: 1280, quality: 0.75 });
      } else {
        dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result); r.onerror = rej;
          r.readAsDataURL(file);
        });
      }
      setForm((f) => ({ ...f, [fieldName]: dataUrl, document_name: file.name }));
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error('Failed to attach file');
    }
  };

  const validate = () => {
    if (!selected) return 'Pick a module';
    for (const f of selected.fields) {
      if (f.type === 'taxonomy') {
        if (!form.category_group || !form.subcategory) return 'Category and subcategory are required';
        if (form.category_group === 'women_project' && !form.women_activity) return 'Women Project activity is required';
        continue;
      }
      if (f.required && (form[f.name] === undefined || form[f.name] === '' || form[f.name] === null)) {
        return `${f.label} is required`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = selected.transform ? selected.transform(form) : { ...form };
      // Coerce numbers
      selected.fields.forEach((f) => {
        if (f.type === 'number' && payload[f.name] !== undefined && payload[f.name] !== '') {
          payload[f.name] = parseFloat(payload[f.name]);
        }
      });
      await axios.post(`${API_URL}${selected.endpoint}`, payload);
      toast.success(`✓ ${selected.label} saved`);
      // Update recents
      const newRecent = [
        { module: selected.id, label: selected.label, when: new Date().toISOString() },
        ...recent.filter((r) => r.module !== selected.id || true),
      ].slice(0, 10);
      try { localStorage.setItem('suubi_qde_recent', JSON.stringify(newRecent)); } catch (_) { /* best-effort */ }
      setRecent(newRecent);
      // Reset for repeat-entry but stay on same module
      const reInit = {};
      selected.fields.forEach((f) => {
        if (f.defaultValue !== undefined) reInit[f.name] = f.defaultValue;
        if (f.type === 'date') reInit[f.name] = new Date().toISOString().slice(0, 10);
      });
      setForm(reInit);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredModules = availableModules.filter((m) =>
    m.label.toLowerCase().includes(search.toLowerCase()),
  );

  const renderField = (f) => {
    const v = form[f.name] ?? '';
    if (f.type === 'taxonomy') {
      return (
        <TaxonomyFieldsEditor
          taxonomy={taxonomy}
          idPrefix="qde"
          value={{
            category_group: form.category_group || '',
            subcategory: form.subcategory || '',
            women_activity: form.women_activity || '',
            details: form.details || {},
          }}
          onChange={(val) => setForm({ ...form, ...val })}
        />
      );
    }
    if (f.type === 'select') {
      const opts = (f.options || []).map((o) =>
        typeof o === 'string' ? { value: o, label: o.replace(/_/g, ' ') } : o,
      );
      return (
        <Select value={v} onValueChange={(val) => setForm({ ...form, [f.name]: val })}>
          <SelectTrigger data-testid={`qde-${f.name}`}>
            <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === 'textarea') {
      return (
        <Textarea
          rows={2}
          value={v}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
          data-testid={`qde-${f.name}`}
        />
      );
    }
    if (f.type === 'file') {
      return (
        <div className="flex items-center gap-2">
          <Input type="file" accept="image/*,application/pdf" onChange={(e) => handleFile(f.name, e.target.files?.[0])}
            data-testid={`qde-${f.name}`} />
          {form[f.name] && (
            <Badge variant="outline">{form.document_name || 'Attached'}</Badge>
          )}
        </div>
      );
    }
    return (
      <Input
        type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
        value={v}
        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
        required={f.required}
        data-testid={`qde-${f.name}`}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="quick-data-entry-dialog">
        <DialogHeader className="p-4 pb-2 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Quick Data Entry
          </DialogTitle>
          <DialogDescription>
            Enter once — system auto-routes to the correct module. Mobile-friendly · low-bandwidth.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-0 max-h-[75vh]">
          {/* Module picker (left rail) */}
          <div className="border-r p-3 space-y-2 max-h-[75vh] overflow-y-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search modules…"
                className="pl-9 h-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="qde-module-search"
              />
            </div>
            {recent.length > 0 && (
              <div className="pt-1">
                <p className="text-xs text-muted-foreground mb-1 px-2">Recent</p>
                <div className="space-y-1">
                  {recent.slice(0, 3).map((r, i) => {
                    const m = MODULES.find((x) => x.id === r.module);
                    if (!m) return null;
                    const Icon = m.icon;
                    return (
                      <button key={`recent-${r.module}-${r.at || i}`} onClick={() => setModuleId(m.id)}
                        className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted text-sm"
                        data-testid={`qde-recent-${m.id}`}>
                        <div className={cn('p-1 rounded', m.color)}><Icon className="h-3 w-3" /></div>
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                <div className="border-t my-2" />
              </div>
            )}
            <p className="text-xs text-muted-foreground px-2">All Modules ({filteredModules.length})</p>
            {filteredModules.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => setModuleId(m.id)}
                  className={cn(
                    'w-full text-left flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors',
                    moduleId === m.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted',
                  )}
                  data-testid={`qde-module-${m.id}`}
                >
                  <div className={cn('p-1.5 rounded', m.color)}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span className="flex-1">{m.label}</span>
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </button>
              );
            })}
          </div>

          {/* Form (right pane) */}
          <div className="p-4 max-h-[75vh] overflow-y-auto">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 text-muted-foreground">
                <ListChecks className="h-12 w-12 mb-3 opacity-30" />
                <p>Pick a module from the left to start entry.</p>
                <p className="text-xs mt-1">{availableModules.length} modules available for your role.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className={cn('p-2 rounded-lg', selected.color)}>
                    <selected.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{selected.label}</h3>
                    <p className="text-xs text-muted-foreground">→ {selected.endpoint}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selected.fields.map((f) => (
                    <div
                      key={f.name}
                      className={cn(
                        'space-y-1',
                        f.type === 'textarea' && 'sm:col-span-2',
                        f.type === 'file' && 'sm:col-span-2',
                      )}
                    >
                      <Label>{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                      {renderField(f)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t p-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {selected && (
            <Button onClick={handleSubmit} disabled={submitting} data-testid="qde-submit-btn">
              {submitting ? 'Saving…' : `Save ${selected.label}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Floating action button
export const QuickDataEntryFAB = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="lg"
        className="fixed bottom-24 right-5 md:bottom-24 md:right-6 rounded-full shadow-lg h-14 w-14 p-0 z-[9999] bg-primary hover:bg-primary/90"
        data-testid="qde-fab"
        title="Quick Data Entry"
      >
        <Zap className="h-6 w-6" />
      </Button>
      <QuickDataEntry open={open} onOpenChange={setOpen} />
    </>
  );
};

export default QuickDataEntry;
